<script setup lang="ts">
import { ConfirmationPopup } from '@metapic-apps/shared'
import { ref } from 'vue'

import { useGetBlocklist, useRemoveEntriesFromBlocklist } from '@/api/blocklist.api'
import BlocklistComponent from '@/components/Advertiser/BlocklistComponent.vue'
import BlocklistForm from '@/components/Advertiser/BlocklistForm.vue'
import { notification } from '@/lib/helpers'
import type { BlockListItemType } from '@/lib/types/blocklist.types'
import type { FilterType } from '@/lib/types/common.types'
import { BoxComponent, TypographyComponent } from '@/ui-library'

const props = defineProps<{ advertiserGroupId?: number; advertiserId: number }>()
const isDeletePopupOpen = ref(false)
const deleteItem = ref<BlockListItemType>()
const filter = ref<FilterType>({
  page: 1,
  query: '',
  size: 6,
})

const { isLoading: isDeleteLoading, removeFromBlocklist, error: deleteError } = useRemoveEntriesFromBlocklist()
const { data, isLoading, totalPages } = useGetBlocklist(props.advertiserId, filter)
const openDeleteCampaignPopup = (item: BlockListItemType) => {
  isDeletePopupOpen.value = true
  deleteItem.value = item
}
const reload = () => (filter.value = { ...filter.value, page: 1 })
const removeItemFromBlocklist = async () => {
  if (deleteItem.value) {
    await removeFromBlocklist(props.advertiserId, deleteItem.value.id)
    isDeletePopupOpen.value = false
    if (!deleteError.value) {
      notification.success(`${deleteItem.value?.user ? 'Creator' : 'Tag'} was unblocked successfully`)
      deleteItem.value = undefined
      reload()
    }
  }
}
</script>
<template>
  <BoxComponent w="100%">
    <BlocklistForm v-if="advertiserGroupId" :advertiser-group-id="advertiserGroupId" :advertiser-id="advertiserId" @reload="reload" />
    <el-divider />
    <BlocklistComponent
      v-model:filter="filter"
      :items="data"
      :total-pages="totalPages"
      :is-loading="isLoading"
      @remove="openDeleteCampaignPopup"
    />
  </BoxComponent>
  <ConfirmationPopup
    v-model="isDeletePopupOpen"
    :is-loading="isDeleteLoading"
    :title="`Unblock ${deleteItem?.userTag ? 'tag' : 'creator'}`"
    submit-btn-text="Unblock"
    submit-btn-variant="primary"
    @on-submit="removeItemFromBlocklist"
  >
    <BoxComponent v-if="deleteItem?.user" :mt="16" :mb="24">
      <TypographyComponent variant="body-large"> Are you sure to unblock this creator? </TypographyComponent>
      <TypographyComponent variant="body-large"> The creator will regain access to the advertiser and its campaigns. </TypographyComponent>
    </BoxComponent>
    <BoxComponent v-else :mt="16" :mb="24">
      <TypographyComponent variant="body-large"> Are you sure to unblock this tag? </TypographyComponent>
      <TypographyComponent variant="body-large">
        All creators associated with this tag will regain access to the advertiser and its campaigns.
      </TypographyComponent>
    </BoxComponent>
  </ConfirmationPopup>
</template>
<style lang="scss" scoped></style>
